import { Box, Typography, Button } from "@mui/material";
import React from "react";
import { sm } from 'src/site-map';
import { Loading, StyledLoadingButton } from "src/ui/components/shared";
import { useRouter } from "next/router";
import { FileSearchIcon } from "src/ui/icons/file-search-icon";
import { Stack } from "@mui/material";
import { BatchStatus } from "src/logic/models/batch";
import { OperationType, useBatchActionWording } from "../../../procurement/batch/util";

interface BackgroundActionBannerProps {
    status: BatchStatus;
    action: string;
    refetch?: () => Promise<any>;
}

const BackgroundActionBanner = ({ status, action, refetch }: BackgroundActionBannerProps) => {
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const isCreated = status === BatchStatus.CREATED;
    const router = useRouter();
    const isManageCatalogueRoute = router.pathname === sm.manageCatalogue.url;
    const isManageBrandsRoute = router.pathname === sm.myCatalogue.myBrands.url;
    const { title } = useBatchActionWording({
        operationType: action as OperationType,
        status: status,
        mode: 'banner',
    });

    return (
        <Box
            sx={{
                my: 2,
                borderRadius: 2,
                padding: 4,
                backgroundColor: '#FFFFFF',
                boxShadow: '14px 17px 40px 4px #7090B014',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                color: '#242833',
                gap: 2
            }}
        >
            {isCreated ? (
                <>
                    <Typography sx={{ fontSize: '21px' }} fontWeight={600}>
                        Preparing your action...
                    </Typography>
                    <Typography variant="body2" fontWeight={400}>
                        This action will take a few minutes to perform.<br />
                        You can freely navigate to other pages while we work on it in the background.
                    </Typography>

                    <Stack alignItems="center" mt={3}>
                        <Loading />
                        <Typography variant="body2" color="#9CA3AF" fontWeight={400} pt={1}>
                            Loading…
                        </Typography>
                    </Stack>
                </>
            ) : (
                <>
                    <Typography sx={{ fontSize: '21px' }} fontWeight={600}>
                        {title || "Processing Action..."}
                    </Typography>

                    <FileSearchIcon />

                    {(!isManageCatalogueRoute && !isManageBrandsRoute) ?
                        <Button
                            variant="outlined"
                            disabled={status !== BatchStatus.COMPLETED && status !== BatchStatus.FAILED}
                            onClick={() => router.push(sm.manageCatalogue.url)}
                            sx={{ mt: 2, fontSize: '13px', fontWeight: 600 }}
                        >
                            Manage My Catalogue
                        </Button> :
                        <StyledLoadingButton
                            loading={isLoading}
                            variant="contained"
                            disabled={status !== BatchStatus.COMPLETED && status !== BatchStatus.FAILED}
                            onClick={async () => {
                                if (!refetch) return;
                                try {
                                    setIsLoading(true);
                                    await refetch();
                                } finally {
                                    setIsLoading(false);
                                }
                            }}
                            style={{ marginTop: 2, fontSize: '13px', fontWeight: 600 }}
                            disableTranslate
                            type={'button'}
                            customWidth
                            translateKey={`Refetch`}
                        />
                    }
                </>
            )
            }
        </Box >
    );
};

export default BackgroundActionBanner;
